import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Film, Music, Search, Star, Trash2, ExternalLink, Play, Clock, HardDrive,
  ChevronDown, Grid3X3, List, RefreshCw, Plus, X, Tag, Volume2, ImageIcon, UserCircle2,
  ArrowLeft, CheckSquare, Square, Download, Loader2, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuthStore } from '../lib/store';
import { batchExportAPI } from '../lib/api';

// Lazy load VideoLibraryPage for the Videos tab
const VideoLibraryContent = lazy(() => import('./VideoLibraryPage'));

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Media type configuration
const MEDIA_TYPES = {
  video: { icon: Film, label: 'Videos', color: 'text-purple-500', bgColor: 'bg-purple-500/10' },
  audio: { icon: Volume2, label: 'Audio', color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  music: { icon: Music, label: 'Music', color: 'text-pink-500', bgColor: 'bg-pink-500/10' },
  image: { icon: ImageIcon, label: 'Images', color: 'text-green-500', bgColor: 'bg-green-500/10' },
  voice_clone: { icon: UserCircle2, label: 'Voice Clones', color: 'text-orange-500', bgColor: 'bg-orange-500/10' }
};

const SOURCE_LABELS = {
  generated: 'AI Generated',
  uploaded: 'Uploaded',
  extracted: 'Extracted',
  cloned: 'Cloned',
  stock: 'Stock'
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

const formatDuration = (seconds) => {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Media Asset Card Component
const MediaAssetCard = ({ asset, onToggleFavorite, onDelete, onSelect, viewMode, isSelectMode, isSelected, onToggleSelect }) => {
  const typeConfig = MEDIA_TYPES[asset.media_type] || MEDIA_TYPES.image;
  const Icon = typeConfig.icon;
  const isCompact = viewMode === 'list';
  
  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    onToggleFavorite(asset.id);
  };
  
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (window.confirm('Delete this asset?')) {
      onDelete(asset.id);
    }
  };
  
  const handleCardClick = () => {
    if (isSelectMode) {
      onToggleSelect(asset.id);
    } else {
      onSelect(asset);
    }
  };
  
  if (isCompact) {
    return (
      <div 
        data-testid={`media-asset-${asset.id}`}
        onClick={handleCardClick}
        className={`flex items-center gap-3 sm:gap-4 p-3 bg-zinc-900/50 rounded-lg border cursor-pointer transition-all group ${
          isSelected ? 'border-purple-500 bg-purple-500/10' : 'border-zinc-800 hover:border-zinc-700'
        }`}
      >
        {/* Selection checkbox */}
        {isSelectMode && (
          <div className="flex-shrink-0">
            {isSelected ? (
              <CheckSquare className="w-5 h-5 text-purple-500" />
            ) : (
              <Square className="w-5 h-5 text-zinc-500" />
            )}
          </div>
        )}
        <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-lg ${typeConfig.bgColor} flex items-center justify-center overflow-hidden flex-shrink-0`}>
          {asset.thumbnail_url ? (
            <img src={asset.thumbnail_url} alt={asset.name} className="w-full h-full object-cover" />
          ) : (
            <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${typeConfig.color}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-medium truncate text-sm sm:text-base">{asset.name}</h3>
          <div className="flex items-center gap-2 sm:gap-3 text-xs text-zinc-500 mt-1 flex-wrap">
            <span className={typeConfig.color}>{typeConfig.label}</span>
            {asset.duration && <span className="hidden sm:flex items-center"><Clock className="inline w-3 h-3 mr-1" />{formatDuration(asset.duration)}</span>}
            <span className="text-zinc-600">{SOURCE_LABELS[asset.source] || asset.source}</span>
          </div>
        </div>
        {!isSelectMode && (
          <div className="flex items-center gap-1 sm:gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={handleFavoriteClick} className={`p-1.5 rounded-full transition-colors ${asset.is_favorite ? 'text-yellow-500 bg-yellow-500/10' : 'text-zinc-500 hover:text-yellow-500'}`}>
              <Star className="w-4 h-4" fill={asset.is_favorite ? 'currentColor' : 'none'} />
            </button>
            <button onClick={handleDeleteClick} className="p-1.5 rounded-full text-zinc-500 hover:text-red-500 hover:bg-red-500/10 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div 
      data-testid={`media-asset-${asset.id}`}
      onClick={handleCardClick}
      className={`bg-zinc-900/50 rounded-xl border cursor-pointer transition-all overflow-hidden group ${
        isSelected ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-zinc-800 hover:border-zinc-700'
      }`}
    >
      <div className={`relative aspect-video ${typeConfig.bgColor} flex items-center justify-center`}>
        {asset.thumbnail_url ? (
          <img src={asset.thumbnail_url} alt={asset.name} className="w-full h-full object-cover" />
        ) : (
          <Icon className={`w-10 h-10 sm:w-12 sm:h-12 ${typeConfig.color}`} />
        )}
        {(asset.media_type === 'audio' || asset.media_type === 'music') && !isSelectMode && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor" />
            </div>
          </div>
        )}
        {asset.duration && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 rounded text-xs text-white">
            {formatDuration(asset.duration)}
          </div>
        )}
        {/* Selection checkbox in grid view */}
        {isSelectMode ? (
          <div className={`absolute top-2 left-2 p-1 rounded-md transition-all ${isSelected ? 'bg-purple-500' : 'bg-black/50'}`}>
            {isSelected ? (
              <CheckSquare className="w-5 h-5 text-white" />
            ) : (
              <Square className="w-5 h-5 text-white/70" />
            )}
          </div>
        ) : (
          <button onClick={handleFavoriteClick} className={`absolute top-2 right-2 p-1.5 rounded-full transition-all ${asset.is_favorite ? 'text-yellow-500 bg-yellow-500/20' : 'text-white/60 bg-black/40 opacity-0 group-hover:opacity-100'}`}>
            <Star className="w-4 h-4" fill={asset.is_favorite ? 'currentColor' : 'none'} />
          </button>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-white font-medium truncate text-sm sm:text-base mb-1">{asset.name}</h3>
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span className={typeConfig.color}>{SOURCE_LABELS[asset.source] || asset.source}</span>
          {asset.file_size && <span className="hidden sm:block">{formatFileSize(asset.file_size)}</span>}
        </div>
        {asset.tags && asset.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {asset.tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-zinc-800 rounded-full text-xs text-zinc-400">{tag}</span>
            ))}
            {asset.tags.length > 3 && <span className="px-2 py-0.5 text-xs text-zinc-500">+{asset.tags.length - 3}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

// Asset Detail Modal
const AssetDetailModal = ({ asset, onClose, onToggleFavorite, onDelete, onAddTag }) => {
  const [newTag, setNewTag] = useState('');
  const typeConfig = MEDIA_TYPES[asset.media_type] || MEDIA_TYPES.image;
  const Icon = typeConfig.icon;
  
  const handleAddTag = () => {
    if (newTag.trim()) {
      onAddTag(asset.id, [newTag.trim()]);
      setNewTag('');
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="bg-zinc-900 rounded-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${typeConfig.bgColor} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${typeConfig.color}`} />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-white truncate">{asset.name}</h2>
              <p className="text-xs sm:text-sm text-zinc-500">{typeConfig.label} • {SOURCE_LABELS[asset.source]}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-3 sm:p-4 overflow-y-auto max-h-[calc(95vh-180px)] sm:max-h-[calc(90vh-160px)]">
          <div className={`aspect-video rounded-xl ${typeConfig.bgColor} flex items-center justify-center mb-4 overflow-hidden`}>
            {asset.media_type === 'audio' || asset.media_type === 'music' ? (
              <div className="w-full p-4">
                <audio src={asset.url?.startsWith('http') ? asset.url : `${API_URL}${asset.url}`} controls className="w-full" />
              </div>
            ) : asset.thumbnail_url ? (
              <img src={asset.thumbnail_url} alt={asset.name} className="w-full h-full object-contain" />
            ) : (
              <Icon className={`w-12 h-12 sm:w-16 sm:h-16 ${typeConfig.color}`} />
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4">
            {asset.duration && (
              <div className="bg-zinc-800/50 rounded-lg p-2 sm:p-3">
                <p className="text-xs text-zinc-500 mb-1">Duration</p>
                <p className="text-white font-medium text-sm sm:text-base">{formatDuration(asset.duration)}</p>
              </div>
            )}
            {asset.file_size && (
              <div className="bg-zinc-800/50 rounded-lg p-2 sm:p-3">
                <p className="text-xs text-zinc-500 mb-1">File Size</p>
                <p className="text-white font-medium text-sm sm:text-base">{formatFileSize(asset.file_size)}</p>
              </div>
            )}
            <div className="bg-zinc-800/50 rounded-lg p-2 sm:p-3">
              <p className="text-xs text-zinc-500 mb-1">Created</p>
              <p className="text-white font-medium text-sm sm:text-base">{new Date(asset.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          
          <div className="mb-4">
            <p className="text-sm text-zinc-400 mb-2">Tags</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {(asset.tags || []).map((tag, i) => (
                <span key={i} className="px-2 sm:px-3 py-1 bg-zinc-800 rounded-full text-xs sm:text-sm text-zinc-300">{tag}</span>
              ))}
              {(!asset.tags || asset.tags.length === 0) && <span className="text-zinc-600 text-sm">No tags</span>}
            </div>
            <div className="flex gap-2">
              <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Add a tag..." className="bg-zinc-800 border-zinc-700 text-white text-sm" onKeyPress={(e) => e.key === 'Enter' && handleAddTag()} />
              <Button onClick={handleAddTag} size="sm" variant="outline" className="flex-shrink-0"><Tag className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0 p-3 sm:p-4 border-t border-zinc-800">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onToggleFavorite(asset.id)} className={`flex-1 sm:flex-initial ${asset.is_favorite ? 'text-yellow-500 border-yellow-500/50' : ''}`}>
              <Star className="w-4 h-4 mr-2" fill={asset.is_favorite ? 'currentColor' : 'none'} />
              {asset.is_favorite ? 'Favorited' : 'Favorite'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => { if (window.confirm('Delete?')) { onDelete(asset.id); onClose(); }}} className="flex-1 sm:flex-initial text-red-500 border-red-500/30 hover:bg-red-500/10">
              <Trash2 className="w-4 h-4 mr-2" />Delete
            </Button>
          </div>
          {asset.url && (
            <Button variant="outline" size="sm" className="flex-1 sm:flex-initial" onClick={() => window.open(asset.url?.startsWith('http') ? asset.url : `${API_URL}${asset.url}`, '_blank')}>
              <ExternalLink className="w-4 h-4 mr-2" />Open
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Tab Button Component
const TabButton = ({ type, label, count, icon: Icon, isActive, onClick, color }) => (
  <button
    onClick={onClick}
    data-testid={`tab-${type}`}
    className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-sm whitespace-nowrap transition-all ${
      isActive 
        ? `bg-zinc-800 text-white border border-zinc-600` 
        : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
    }`}
  >
    <Icon className={`w-4 h-4 ${isActive ? color : ''}`} />
    <span className="hidden sm:inline">{label}</span>
    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-zinc-700' : 'bg-zinc-800'}`}>
      {count}
    </span>
  </button>
);

// Main Component
export default function MediaLibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuthStore();
  
  const initialType = searchParams.get('type') || 'all';
  
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState(initialType);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0, hasMore: false });
  
  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  // Fetch media assets
  const fetchAssets = useCallback(async (reset = false) => {
    if (selectedType === 'video') {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedType && selectedType !== 'all') params.append('media_type', selectedType);
      if (favoritesOnly) params.append('favorites_only', 'true');
      if (searchQuery) params.append('search', searchQuery);
      params.append('skip', reset ? '0' : pagination.skip.toString());
      params.append('limit', pagination.limit.toString());
      
      const response = await fetch(`${API_URL}/api/media?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch assets');
      
      const data = await response.json();
      
      if (reset) {
        setAssets(data.assets || []);
      } else {
        setAssets(prev => [...prev, ...(data.assets || [])]);
      }
      
      setPagination({
        skip: data.skip || 0,
        limit: data.limit || 50,
        total: data.total || 0,
        hasMore: data.has_more || false
      });
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  }, [token, selectedType, favoritesOnly, searchQuery, pagination.skip, pagination.limit]);
  
  // Fetch stats and trigger legacy sync
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/media/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
      
      // If we have legacy assets that haven't been synced, trigger sync
      const hasLegacyVideos = data?.video?.legacy_count > 0;
      const hasLegacyImages = data?.image?.legacy_count > 0;
      const hasLegacyVoices = data?.voice_clone?.legacy_count > 0;
      
      if (hasLegacyVideos || hasLegacyImages || hasLegacyVoices) {
        // Trigger background sync (don't await, let it run)
        fetch(`${API_URL}/api/media/sync-legacy`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          if (res.ok) {
            // Refresh stats after sync completes
            setTimeout(() => fetchStats(), 2000);
          }
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);
  
  // Initial load
  useEffect(() => {
    fetchAssets(true);
    fetchStats();
  }, [selectedType, favoritesOnly]);
  
  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedType !== 'video') fetchAssets(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Handle type change
  const handleTypeChange = (type) => {
    setSelectedType(type);
    setSearchParams(type === 'all' ? {} : { type });
  };
  
  // Toggle favorite
  const handleToggleFavorite = async (assetId) => {
    try {
      const response = await fetch(`${API_URL}/api/media/${assetId}/toggle-favorite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, is_favorite: data.is_favorite } : a));
      if (selectedAsset?.id === assetId) setSelectedAsset(prev => ({ ...prev, is_favorite: data.is_favorite }));
      toast.success(data.is_favorite ? 'Added to favorites' : 'Removed from favorites');
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };
  
  // Delete asset
  const handleDelete = async (assetId) => {
    try {
      const response = await fetch(`${API_URL}/api/media/${assetId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed');
      setAssets(prev => prev.filter(a => a.id !== assetId));
      fetchStats();
      toast.success('Asset deleted');
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };
  
  // Add tags
  const handleAddTag = async (assetId, tags) => {
    try {
      const response = await fetch(`${API_URL}/api/media/${assetId}/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tags })
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, tags: data.tags } : a));
      if (selectedAsset?.id === assetId) setSelectedAsset(prev => ({ ...prev, tags: data.tags }));
      toast.success('Tag added');
    } catch (error) {
      toast.error('Failed to add tag');
    }
  };
  
  // Multi-select handlers
  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    if (isSelectMode) {
      setSelectedIds(new Set()); // Clear selections when exiting select mode
    }
  };
  
  const toggleAssetSelection = (assetId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };
  
  const selectAll = () => {
    setSelectedIds(new Set(assets.map(a => a.id)));
  };
  
  const clearSelection = () => {
    setSelectedIds(new Set());
  };
  
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    const confirmed = window.confirm(`Are you sure you want to delete ${selectedIds.size} asset(s)? This cannot be undone.`);
    if (!confirmed) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/media/bulk-delete`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ asset_ids: Array.from(selectedIds) })
      });
      
      if (!response.ok) throw new Error('Failed to delete assets');
      
      const data = await response.json();
      
      // Remove deleted assets from state
      setAssets(prev => prev.filter(a => !selectedIds.has(a.id)));
      setSelectedIds(new Set());
      setIsSelectMode(false);
      fetchStats();
      
      toast.success(`Successfully deleted ${data.deleted || selectedIds.size} asset(s)`);
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete some assets');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Batch export videos as ZIP
  const handleBatchExport = async () => {
    if (selectedIds.size === 0) return;
    
    // For video exports, we need to get video IDs
    // Note: This works when in video tab or when video assets are selected
    const videoIds = Array.from(selectedIds);
    
    if (videoIds.length > 25) {
      toast.error('Maximum 25 videos per export batch');
      return;
    }
    
    setIsExporting(true);
    const loadingToast = toast.loading(`Preparing ${videoIds.length} video(s) for download...`);
    
    try {
      const response = await batchExportAPI.exportVideos(videoIds);
      
      if (response.data.success && response.data.download_url) {
        toast.dismiss(loadingToast);
        
        // Show success with download link
        toast.success(
          <div className="flex flex-col gap-2">
            <span>ZIP ready! {response.data.videos_included} video(s) included</span>
            <a 
              href={response.data.download_url}
              download
              className="text-[#00F0FF] underline hover:text-[#00F0FF]/80 flex items-center gap-1"
              onClick={() => toast.dismiss()}
            >
              <Download className="w-4 h-4" />
              Download ZIP
            </a>
            {response.data.videos_failed > 0 && (
              <span className="text-xs text-amber-500">
                {response.data.videos_failed} video(s) could not be included
              </span>
            )}
          </div>,
          { duration: 15000 }
        );
        
        // Also auto-trigger download
        window.open(response.data.download_url, '_blank');
        
        // Clear selection
        setSelectedIds(new Set());
        setIsSelectMode(false);
      } else {
        toast.dismiss(loadingToast);
        toast.error('Failed to create export');
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Batch export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export videos');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Calculate totals from stats (which now includes legacy counts)
  const totalVideos = stats?.video?.count || 0;
  const totalImages = stats?.image?.count || 0;
  const totalAudio = stats?.audio?.count || 0;
  const totalMusic = stats?.music?.count || 0;
  const totalVoiceClones = stats?.voice_clone?.count || 0;
  const totalAssets = totalVideos + totalImages + totalAudio + totalMusic + totalVoiceClones;
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="media-library-page">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                <HardDrive className="w-6 h-6 sm:w-7 sm:h-7 text-purple-500" />
                Media Library
              </h1>
              <p className="text-xs sm:text-sm text-zinc-500 mt-1">All your content in one place</p>
            </div>
            <div className="flex gap-2 self-start sm:self-auto">
              {/* Multi-select controls */}
              {selectedType !== 'video' && (
                <>
                  {isSelectMode ? (
                    <>
                      <Button 
                        onClick={selectAll} 
                        variant="outline" 
                        size="sm" 
                        className="border-zinc-700"
                        disabled={assets.length === 0}
                      >
                        Select All
                      </Button>
                      {selectedIds.size > 0 && (
                        <>
                          <Button 
                            onClick={handleBatchExport} 
                            variant="outline" 
                            size="sm"
                            className="border-zinc-700 text-[#00F0FF] border-[#00F0FF]/50 hover:bg-[#00F0FF]/10"
                            disabled={isExporting}
                            data-testid="batch-export-btn"
                          >
                            {isExporting ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Package className="w-4 h-4 mr-2" />
                            )}
                            Export ({selectedIds.size})
                          </Button>
                          <Button 
                            onClick={handleBulkDelete} 
                            variant="destructive" 
                            size="sm"
                            disabled={isDeleting}
                          >
                            <Trash2 className={`w-4 h-4 mr-2 ${isDeleting ? 'animate-pulse' : ''}`} />
                            Delete ({selectedIds.size})
                          </Button>
                        </>
                      )}
                      <Button 
                        onClick={toggleSelectMode} 
                        variant="outline" 
                        size="sm" 
                        className="border-zinc-700"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button 
                      onClick={toggleSelectMode} 
                      variant="outline" 
                      size="sm" 
                      className="border-zinc-700"
                      disabled={assets.length === 0}
                    >
                      <CheckSquare className="w-4 h-4 mr-2" />
                      Select
                    </Button>
                  )}
                </>
              )}
              <Button onClick={() => { fetchAssets(true); fetchStats(); }} variant="outline" size="sm" className="border-zinc-700">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
              </Button>
            </div>
          </div>
          
          {/* Tabs - Always visible */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-hide">
            <TabButton type="all" label="All" count={totalAssets} icon={Grid3X3} isActive={selectedType === 'all'} onClick={() => handleTypeChange('all')} color="text-white" />
            <TabButton type="video" label="Videos" count={totalVideos} icon={Film} isActive={selectedType === 'video'} onClick={() => handleTypeChange('video')} color="text-purple-500" />
            <TabButton type="image" label="Images" count={totalImages} icon={ImageIcon} isActive={selectedType === 'image'} onClick={() => handleTypeChange('image')} color="text-green-500" />
            <TabButton type="audio" label="Audio" count={totalAudio} icon={Volume2} isActive={selectedType === 'audio'} onClick={() => handleTypeChange('audio')} color="text-blue-500" />
            <TabButton type="music" label="Music" count={totalMusic} icon={Music} isActive={selectedType === 'music'} onClick={() => handleTypeChange('music')} color="text-pink-500" />
            <TabButton type="voice_clone" label="Voice Clones" count={totalVoiceClones} icon={UserCircle2} isActive={selectedType === 'voice_clone'} onClick={() => handleTypeChange('voice_clone')} color="text-orange-500" />
          </div>
        </div>
      </div>
      
      {/* Content */}
      {selectedType === 'video' ? (
        /* Embedded Video Library */
        <Suspense fallback={
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
          </div>
        }>
          <VideoLibraryContent embedded={true} />
        </Suspense>
      ) : (
        /* Other Media Types */
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name or tags..." className="pl-10 bg-zinc-900 border-zinc-800 text-white text-sm" data-testid="search-input" />
            </div>
            <div className="flex gap-2">
              <Button variant={favoritesOnly ? 'default' : 'outline'} size="sm" onClick={() => setFavoritesOnly(!favoritesOnly)} className={`flex-1 sm:flex-initial ${favoritesOnly ? 'bg-yellow-600 hover:bg-yellow-700' : 'border-zinc-700'}`} data-testid="favorites-filter">
                <Star className="w-4 h-4 sm:mr-2" fill={favoritesOnly ? 'currentColor' : 'none'} />
                <span className="hidden sm:inline">Favorites</span>
              </Button>
              <div className="flex border border-zinc-700 rounded-lg overflow-hidden">
                <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`} data-testid="view-grid">
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`} data-testid="view-list">
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Assets Grid/List */}
          {loading && assets.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12 sm:py-20">
              <HardDrive className="w-12 h-12 sm:w-16 sm:h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-zinc-400 mb-2">No assets found</h3>
              <p className="text-sm text-zinc-600 mb-6 px-4">
                {searchQuery ? 'Try a different search term' : selectedType && selectedType !== 'all' ? `No ${MEDIA_TYPES[selectedType]?.label.toLowerCase() || 'assets'} yet` : 'Start creating content to build your media library'}
              </p>
            </div>
          ) : (
            <>
              <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4' : 'flex flex-col gap-2'}>
                {assets.map(asset => (
                  <MediaAssetCard 
                    key={asset.id} 
                    asset={asset} 
                    onToggleFavorite={handleToggleFavorite} 
                    onDelete={handleDelete} 
                    onSelect={setSelectedAsset} 
                    viewMode={viewMode}
                    isSelectMode={isSelectMode}
                    isSelected={selectedIds.has(asset.id)}
                    onToggleSelect={toggleAssetSelection}
                  />
                ))}
              </div>
              {pagination.hasMore && (
                <div className="flex justify-center mt-8">
                  <Button onClick={() => { setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit })); fetchAssets(false); }} variant="outline" className="border-zinc-700" disabled={loading}>
                    {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ChevronDown className="w-4 h-4 mr-2" />}Load More
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* Asset Detail Modal */}
      {selectedAsset && (
        <AssetDetailModal asset={selectedAsset} onClose={() => setSelectedAsset(null)} onToggleFavorite={handleToggleFavorite} onDelete={handleDelete} onAddTag={handleAddTag} />
      )}
    </div>
  );
}
